import React from 'react';
import { useQueryStates, parseAsString, parseAsInteger } from 'nuqs';
import { useAtom } from 'jotai';
import { errorzHydratedAtom, errorzListParamsAtom } from '@/store';

export interface SearchFields {
  message: string;
  ip: string;
}

export function useErrorzFilters() {
  const [params, setParams] = useAtom(errorzListParamsAtom);
  const [, setHydrated] = useAtom(errorzHydratedAtom);

  const [queryParams, setQueryParams] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    stage: parseAsString.withDefault('all'),
    status: parseAsString.withDefault('all'),
    message: parseAsString.withDefault(''),
    ip: parseAsString.withDefault('')
  });

  // Filters state (client UI) mapped to API params (URL synced)
  const [stageFilter, setStageFilter] = React.useState<string>(queryParams.stage);
  const [statusFilter, setStatusFilter] = React.useState<string>(queryParams.status);
  const [searchFields, setSearchFields] = React.useState<SearchFields>({
    message: queryParams.message,
    ip: queryParams.ip
  });

  // Manage Search dropdown open to close after apply
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);

  // Sync URL parameters with params atom on mount and when URL changes
  React.useEffect(() => {
    const filters: Record<string, string | undefined> = {};

    // Build filters from URL parameters
    if (queryParams.stage !== 'all') filters.stage = queryParams.stage;
    if (queryParams.status !== 'all') filters.status = queryParams.status;
    if (queryParams.message) filters.message = queryParams.message;
    if (queryParams.ip) filters.ip = queryParams.ip;

    // Only update if there's actually a change to avoid unnecessary API calls
    setParams((prev) => {
      const newParams = {
        ...prev,
        page: queryParams.page,
        filters: filters
      };

      // Check if anything actually changed
      if (prev.page === newParams.page &&
        JSON.stringify(prev.filters) === JSON.stringify(newParams.filters)) {
        return prev; // No change, return same object
      }

      return newParams;
    });

    // Mark hydrated after first effective sync
    setHydrated(true);
  }, [queryParams, setParams]);

  // Sync URL parameters with local state on mount
  React.useEffect(() => {
    setStageFilter(queryParams.stage);
    setStatusFilter(queryParams.status);
    setSearchFields({
      message: queryParams.message,
      ip: queryParams.ip
    });
  }, []); // Only run on mount

  const applyFilters = React.useCallback(async () => {
    const nextFilters: Record<string, string | undefined> = {
      ...params.filters,
      stage: stageFilter !== 'all' ? stageFilter : undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      message: searchFields.message || undefined,
      ip: searchFields.ip || undefined
    };

    setParams((prev) => ({ ...prev, page: 1, filters: nextFilters }));

    await setQueryParams({
      page: 1,
      stage: stageFilter,
      status: statusFilter,
      message: searchFields.message,
      ip: searchFields.ip
    });

    setIsSearchOpen(false);
  }, [params.filters, stageFilter, statusFilter, searchFields, setParams, setQueryParams]);

  const clearFilters = React.useCallback(async () => {
    setStageFilter('all');
    setStatusFilter('all');
    setSearchFields({ message: '', ip: '' });
    setParams((prev) => ({ ...prev, page: 1, filters: {} }));
    await setQueryParams({
      page: 1,
      stage: 'all',
      status: 'all',
      message: '',
      ip: ''
    });
  }, [setParams, setQueryParams]);

  const hasAnyFilter = React.useMemo(() => {
    return (
      stageFilter !== 'all' ||
      statusFilter !== 'all' ||
      Boolean(searchFields.message || searchFields.ip)
    );
  }, [stageFilter, statusFilter, searchFields]);

  const handleStageChange = React.useCallback((val: string) => {
    setStageFilter(val);
    setQueryParams({ page: 1, stage: val });
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, stage: val !== 'all' ? val : undefined }
    }));
  }, [setQueryParams, setParams]);

  const handleStatusChange = React.useCallback((val: string) => {
    setStatusFilter(val);
    setQueryParams({ page: 1, status: val });
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, status: val !== 'all' ? val : undefined }
    }));
  }, [setQueryParams, setParams]);

  const handlePageChange = React.useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }));
    setQueryParams({ page });
  }, [setParams, setQueryParams]);

  return {
    // State
    stageFilter,
    statusFilter,
    searchFields,
    setSearchFields,
    isSearchOpen,
    setIsSearchOpen,
    hasAnyFilter,

    // Actions
    applyFilters,
    clearFilters,
    handleStageChange,
    handleStatusChange,
    handlePageChange
  };
}
